import { useEffect, useRef, useState } from "react"
import { publicSaleId } from '../../../utils/saleId'
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { Grid, Button, CircularProgress, Skeleton } from "@mui/material"
import { ISettingsItem, useSettings } from "../../../Settings"
import { useForm } from "../../../Form"
import { defaultPlan, usePlan } from '../../../Plans'
import { useInitReq } from "../../../InitReq"
import { interpolate, useTranslation } from "../../../i18n"
import { useNotification } from "../../../Notification"
import requests from "../../../api/client"
import OtpModal from "../../otpModal"
import { ReactComponent as RightArrow } from '../../../assets/icons/rightArrow.svg'
import { CanaisOtp, OtpInitData, ValidateOtpOk } from "../../../types/otp"
import useCountdown from "../../../hooks/useCountdown"
import checkoutResumo from "../../../utils/card/checkoutSummary"
import cartaoPagar, { ThreeDS } from "../../../utils/card/paymentCard"
import fetchCardRegister from "../../../utils/card/fetchCardRegister"
import criarCacheTokenCartao from "../../../utils/card/cacheTokenCartao"
import use3DS from "../../../utils/card/use3DS"
import esperarSdk3DS from "../../../utils/card/esperarSdk3DS"
import { lerCheckout, limparCheckout, salvarCheckout } from "../../../utils/card/checkoutPersistido"
import classifyPaymentFailure from "../../../utils/card/classifyPaymentFailure"
import isPaymentApproved from "../../../utils/card/isPaymentApproved"
import { resolveCartPlan } from "../../../utils/planVisibility"
import { shouldSendReferralCode } from "../../../utils/referralVoucher"
import { isStorePickupSelected } from "../../../utils/storePickup"
import { formatarReais, valorParcela } from "../../../utils/installments"
import SuccessfullyPaid from "../../../utils/successfullyPaid"
import { normalizeApiError } from "../../../api/normalizeApiError"
import { traduzirCampos } from "../../../api/fieldMap"
import { useApiErrorMessage, useRetryAfterMessage } from "../../../api/useApiErrorMessage"

const MOCK = process.env.REACT_APP_MOCK === "true"

/** Máximo de tentativas de pagamento (contador local; vide contrato). */
const MAX_TENTATIVAS = 3

/**
 * A retomada da compra guardada acontece uma vez por carregamento da página
 * (o F5 zera o módulo); navegar dentro do app não a dispara de novo.
 */
let retomadaTentada = false

export default function PurchaseSummary({ name, variables }: ISettingsItem) {
  const [reqLoading, setReqLoading] = useState(false)
  const [otpOpen, setOtpOpen] = useState(false)
  const [otpData, setOtpData] = useState<OtpInitData | null>(null)
  const {
    currentFields: {
      cpfCnpj,
      date,
      externalId,
      email,
      ddd,
      telephone,
      street,
      number,
      complement,
      neighborhood,
      cep,
      city,
      uf,
      cityId,
      paymentMethod,
      keepCardChecked,
      cardName,
      cardNumber,
      cardIssueDate,
      cardSecurityCode,
      cardInstallments,
      cartNavigate,
      paymentOption,
      installmentLink,
      isVoucherValid,
      voucherClub,
      cartId,
      hash,
      purchaseSummaryWithOtp
    },
    currentFields,
    formState: { form1, form2, form3, cardForm, disabledBtn },
    setFormState,
    setModelledField,
    updateFormState
  } = useForm()
  const { pageTitle, socials } = useSettings()
  const { cartInfo, couponText, couponValid, couponValue, couponPercent, physicalChipAvailable, virtualStock, referralVoucherEnabled, chipInfo: { chipPrice, shippingPrice, pickUpInStore }, ip, updateInitState } = useInitReq()
  const { t } = useTranslation()
  const noStock = !physicalChipAvailable && !virtualStock
  const sendReferralCode = shouldSendReferralCode({ isVoucherValid, referralVoucherEnabled })
  const {
    loadingPlan,
    currentPlan,
    chosenPlan,
    monthlyPrice,
    annualPrice,
    pickUpChip,
    virtualChip,
    portability,
    chipOnly,
    smartControl,
    planIndex,
    step,
    chipQuantity,
    currentChipPrice,
    currentShippingPrice,
    totalPrice,
    plans,
    updatePlanState,
    setvirtualChip,
    setPortability,
    setSmartControl,
  } = usePlan()
  const storePickupSelected = isStorePickupSelected({ pickUpChip, pickUpInStore })
  const defaultTextArray = [t.page2.summary.total, t.common.continue, t.common.submit]
  const { text = defaultTextArray } = variables || {}
  const navigate = useNavigate()
  const { notify } = useNotification()
  const mensagemDeErro = useApiErrorMessage()
  const mensagemDeEspera = useRetryAfterMessage()
  const formValido = Boolean(form3)

  const [pagando, setPagando] = useState(false)
  const [tentativas, setTentativas] = useState(MAX_TENTATIVAS)
  const [tresDSPronto, setTresDSPronto] = useState(false)
  const [retomado, setRetomado] = useState(false)
  const { expirado } = useCountdown(purchaseSummaryWithOtp?.expira_em ?? "")

  // Compra retomada após F5: o plano não está mais em memória, então o valor
  // vem do resumo da própria compra (o mesmo que a API cobra).
  const valorCartao = retomado ? Number(purchaseSummaryWithOtp?.valor) || 0 : totalPrice

  // UnixCard do plano anual parcelado: a cobrança acontece na API em `numero_parcelas`.
  const unixcardParcelado = chosenPlan === 'annual' && paymentOption === 'installment' && paymentMethod === 'UNIXCARD'
  const numeroParcelas = Number(cardInstallments)

  const resolvePagamentoRef = useRef<(() => void) | null>(null)
  const tokenCartaoRef = useRef<string | null>(null)
  const [cacheToken] = useState(criarCacheTokenCartao)
  const pagamentoAtivoRef = useRef(false)

  useEffect(() => {
    const parsedChipPrice = parseFloat(currentChipPrice.replace(',', '.')) || 0
    const parsedShippingPrice = (virtualChip || storePickupSelected) ? 0 : parseFloat(currentShippingPrice.replace(',', '.')) || 0
    const currentPricePlan = chosenPlan === 'monthly' ? monthlyPrice : chosenPlan === 'annual' ? annualPrice * 12 : 0
    let totalPriceCalc = currentPricePlan + parsedChipPrice + parsedShippingPrice

    if (couponValid && couponValue > 0) {
      if (couponPercent) {
        totalPriceCalc = totalPriceCalc - (totalPriceCalc * (couponValue / 100))
      } else {
        totalPriceCalc = totalPriceCalc - couponValue
      }
    }

    const totalPrice = totalPriceCalc

    updatePlanState({ totalPrice })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annualPrice, monthlyPrice, chosenPlan, virtualChip, chipPrice, shippingPrice, chipOnly, currentChipPrice, currentShippingPrice, storePickupSelected, couponValid, couponValue, couponPercent])

  function cleanField(texto: string) { return texto.replace(/[^\d]/g, '') }

  function formattedTel(ddd: string, tel: string) {
    const cleanDDD = cleanField(ddd)
    const cleanTel = cleanField(tel)
    return `${cleanDDD}${cleanTel}`
  }

  let cpfOrCnpjField
  if (cpfCnpj.length === 18) {
    cpfOrCnpjField = { cnpj: cleanField(cpfCnpj) }
  } else {
    cpfOrCnpjField = { cpf: cleanField(cpfCnpj) }
  }

  let birthdayDate
  if (cpfCnpj.length === 18) {
    const formattedDate = dayjs(date).format('YYYY-MM-DD')
    birthdayDate = dayjs(formattedDate).isValid() ? formattedDate : undefined
  } else {
    birthdayDate = dayjs(date).format('YYYY-MM-DD')
  }
  
  const cardRegisterJson = {
    documento: cleanField(cpfCnpj),
    nome_titular: cardName,
    numero_cartao: cardNumber.replace(/ /g, ""),
    mes_ano_vencimento: dayjs(cardIssueDate).format("MM/YYYY"),
    cvv: cardSecurityCode,
  }

  const buyJson = {
    metodo_pagamento: paymentMethod,
    link_indicacao: localStorage.getItem("VipParam"),
    ...(paymentMethod === "CARTAO" && {
      ip,
    }),
    ...(paymentMethod === "UNIXCARD" && {
      cartao: {
        titular: cardName,
        numero: cardNumber.replace(/ /g, ""),
        data_expiracao: dayjs(cardIssueDate).format("MM/YYYY"),
        codigo_verificacao: cardSecurityCode
      }
    }),
    deseja_portabilidade: portability,
    ...couponValid && { cupom: couponText },
    ...sendReferralCode && { codigo_indicacao: voucherClub },
    id_externo: externalId,
    id_plano: currentPlan?.id,
    quantidade: 1,
    nome: currentFields.name,
    email,
    telefone: formattedTel(ddd, telephone),
    ...(birthdayDate && { data_nascimento: birthdayDate }),
    retirada_mvno: storePickupSelected,
    endereco: {
      rua: street,
      numero: number,
      complemento: complement,
      bairro: neighborhood,
      cep: cleanField(cep),
      id_cidade: cityId
    },
    sistema_origem: pageTitle,
    ...cpfOrCnpjField
  }

  const activationJson = {
    metodo_pagamento: paymentMethod,
    link_indicacao: localStorage.getItem("VipParam"),
    ...(chosenPlan === 'annual' && {
      id_plano_promocao: paymentOption === 'oneTime' ? 1 : 2
    }),
    ...(paymentMethod === "CARTAO" && {
      ip,
      ...(keepCardChecked && {
        recarga_automatica_cartao: currentPlan?.id
      })
    }),
    ...(paymentMethod === "UNIXCARD" && {
      cartao: {
        titular: cardName,
        numero: cardNumber.replace(/ /g, ""),
        data_expiracao: dayjs(cardIssueDate).format("MM/YYYY"),
        codigo_verificacao: cardSecurityCode
      }
    }),
    ...(unixcardParcelado && {
      numero_parcelas: numeroParcelas
    }),
    deseja_portabilidade: portability,
    ...couponValid && { cupom: couponText },
    ...sendReferralCode && { codigo_indicacao: voucherClub },
    id_externo: externalId,
    nome: currentFields.name,
    email,
    telefone: formattedTel(ddd, telephone),
    ...(birthdayDate && { data_nascimento: birthdayDate }),
    endereco: {
      rua: street,
      numero: number,
      complemento: complement,
      bairro: neighborhood,
      cep: cleanField(cep),
      id_cidade: cityId
    },
    chips: [
      {
        esim: "SIM",
        id_plano: currentPlan?.id,
        ddd: cleanField(ddd)
      }
    ],
    sistema_origem: pageTitle,
    ...cpfOrCnpjField
  }

  const chipOnlyJson = {
    metodo_pagamento: paymentMethod,
    link_indicacao: localStorage.getItem("VipParam"),
    token: undefined,
    ...couponValid && { cupom: couponText },
    ...sendReferralCode && { codigo_indicacao: voucherClub },
    ip,
    id_externo: externalId,
    id_plano: 88888,
    quantidade: chipQuantity,
    nome: currentFields.name,
    email,
    telefone: formattedTel(ddd, telephone),
    ...(birthdayDate && { data_nascimento: birthdayDate }),
    retirada_mvno: storePickupSelected,
    endereco: {
      rua: street,
      numero: number,
      complemento: complement,
      bairro: neighborhood,
      cep: cleanField(cep),
      id_cidade: cityId
    },
    sistema_origem: pageTitle,
    ...cpfOrCnpjField
  }

  async function handleRecaptchaExecute(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      (window as any).grecaptcha.enterprise.ready(async () => {
        try {
          const token = await (window as any).grecaptcha.enterprise.execute(
            process.env.REACT_APP_RECAPTCHA_SITE_KEY,
            { action: 'PAYMENT' }
          )
          resolve(token)
        } catch (error) {
          reject(error)
        }
      })
    })
  }

  async function fetchPurchase(captchaToken?: string, cardReq: boolean = false) {
    try {
      const body = chipOnly ? chipOnlyJson : virtualChip ? activationJson : buyJson
      const requestFn = chipOnly || !virtualChip
        ? (data: any) => requests.buyRegister(data, cardReq)
        : (data: any) => requests.activationReq(data, cardReq)

      const requestBody = cardReq
        ? { ...body, recaptcha_token: captchaToken }
        : body

      return await requestFn(requestBody)
    } catch (error) {
      // Relança: quem chama decide o que fazer. Antes esta função notificava e
      // devolvia undefined, o que gerava uma segunda notificação "Erro:
      // undefined" e um TypeError no destructuring dos call sites.
      throw normalizeApiError(error)
    }
  }

  /** Notificação única de falha de compra, com os campos soltos do 422 anexados. */
  function notificarFalhaDeCompra(error: unknown) {
    const apiError = normalizeApiError(error)
    const { fieldErrors, semCampo } = traduzirCampos(apiError.fields)

    updateFormState({ fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : null })

    const base = mensagemDeErro(apiError, t.errors.purchaseUnknown)
    const espera = mensagemDeEspera(apiError)

    notify([base, semCampo.join(' '), espera].filter(Boolean).join(' '), 'error')
  }

  // ── Callbacks do modal ──────────────────────────────────────────────────
  function handleOtpSuccess(payload: ValidateOtpOk) {
    setOtpOpen(false)
    salvarCheckout({
      hash: payload.hash_unico,
      cartId,
      virtualChip,
      plano: { planIndex, chosenPlan, pickUpChip, smartControl, portability, chipOnly, chipQuantity },
      cupom: couponValid ? { texto: couponText, valor: couponValue, percentual: couponPercent } : null,
    })
    updateFormState({ hash: payload.hash_unico })
    updatePlanState({ step: step + 1 })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handleOtpResend(_canais: CanaisOtp, _expira_em: string) {}

  function handleOtpCancel() {
    setOtpOpen(false)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handleOtpError(_motivo: string) {
    setOtpOpen(false)
  }

  async function saleSubmit(captchaToken?: string) {
    try {
      if (paymentMethod === 'CARTAO') {
        if (!captchaToken) {
          notify(t.errors.recaptchaFailed, 'error')
          return
        }

        // CARD
        if (chosenPlan === 'annual' && paymentOption === "installment") {
          if (installmentLink === '') {
            setReqLoading(true)
              fetchPurchase(captchaToken, true)
                .then((res) => {
                  const installmentLink = res.link
                  const currentId = publicSaleId(res, virtualChip)

                  updateFormState({ clientId: currentId, installmentLink, fieldErrors: null })
                  updatePlanState({ step: step + 1 })
                })
                .catch(notificarFalhaDeCompra)
                .finally(() => {
                  setReqLoading(false)
                })
          } else {
            updatePlanState({ step: 4 })
            setReqLoading(false)
          }
        } else {
          setReqLoading(true)

          await fetchPurchase(captchaToken, true)
            .then(({ otp_session_token, canais, expira_em }) => {
              updateFormState({ fieldErrors: null })
              setOtpData({ otp_session_token, canais, expira_em })
              setOtpOpen(true)
            })
            .catch(notificarFalhaDeCompra)
            .finally(() => {
              setReqLoading(false)
            })
        }
      } else {
        if (paymentMethod === 'UNIXCARD' && !cardForm) {
          setFormState("showError", true)
          return
        }

        // PIX / BOLETO / UNIXCARD
        setReqLoading(true)
        fetchPurchase()
          .then((res) => {
            const currentId = publicSaleId(res, virtualChip)

            updateFormState({ fieldErrors: null })
            setModelledField('clientId', currentId)

            if (paymentMethod === 'PIX') {
              sessionStorage.setItem(
                'pixData',
                JSON.stringify({
                  idClient: currentId,
                  pixQrCode: res?.pix_QRCode,
                  pixCopyCode: res?.pix_copia_cola,
                  pixLoading: false
                })
              )
              updatePlanState({ step: step + 1 })
            } else if (paymentMethod === 'BOLETO') {
              sessionStorage.setItem(
                'boletoData',
                JSON.stringify({
                  idClient: currentId,
                  boletoBardCode: res?.boleto_codigo_barras,
                  boletoLink: res?.boleto_link,
                  boletoLoading: false
                })
              )
              updatePlanState({ step: step + 1 })
            } else if (paymentMethod === 'UNIXCARD') {
              SuccessfullyPaid({ cartId, clientId: currentId, virtualChip, navigate })
            }
          })
          .catch(notificarFalhaDeCompra)
          .finally(() => {
            setReqLoading(false)
          })
      }
    } catch (error) {
      notify(`${t.errors.purchaseFailed}: ${error}`, 'error')
      setReqLoading(false)
    }
  }

  async function cartRegister() {
    setReqLoading(true)
    const cart = {
      origem: pageTitle,
      ip,
      navegador: "",
      email,
      telefone: formattedTel(ddd, telephone),
      documento: cpfCnpj,
      nome: currentFields.name,
      data_nascimento: dayjs(date).format('YYYY-MM-DD'),
      cep: cleanField(cep),
      cidade: city,
      estado: uf,
      bairro: neighborhood,
      rua: street,
      numero: number,
      complemento: complement,
      id_plano: currentPlan?.id,
      esim: virtualChip,
      deseja_portabilidade: portability ? "1" : "0",
    }
    const response = await requests.cartRegister(cart)
    setReqLoading(false)
    return response
  }

  const handleNext = async () => {
    if (step === 2 && form3 && !cartNavigate && !MOCK) {
      const { success, data } = await cartRegister()
      if (success) updateFormState({ cartId: data.token })
    }

    if (step === 3) {
      if (noStock) {
        window.open(`https://wa.me/${socials?.whatsapp}`, "_blank")
        return
      }

      // Fluxo reiniciado ao sair da página (Plans): o carrinho antigo foi
      // descartado e um novo é criado aqui, com o plano atual.
      if (!cartId && !MOCK) {
        const { success, data } = await cartRegister()
        if (success) updateFormState({ cartId: data.token })
      }

      if (paymentMethod === "PIX" || paymentMethod === "BOLETO") {
        setFormState('disabledBtn', true)
        await saleSubmit()
        setFormState('disabledBtn', false)
        return
      }

      // Cartão parcelado gera aqui o link do checkout. O UnixCard parcelado
      // só cobra no step 5, então segue para o próximo passo sem requisição.
      if (paymentMethod === "CARTAO") {
        setFormState('disabledBtn', true)
        await saleSubmit(await handleRecaptchaExecute())
        setFormState('disabledBtn', false)
        return
      }
    }
  
    if (step === 4) {
      if (paymentMethod === "CARTAO") {
        if (paymentOption === "installment") {
          window.open(installmentLink, "_blank")
          return
        }

        setFormState('disabledBtn', true)
        await handlePagar()
        setFormState('disabledBtn', false)
        return
      }

      if (paymentMethod === "UNIXCARD") {
        setFormState('disabledBtn', true)
        await saleSubmit()
        setFormState('disabledBtn', false)
        return
      }

      if (paymentMethod === "BOLETO") {
        navigate('/pedido-finalizado')
        return
      }
    }
  
    const nextStep = 
      step === 0 ||
      (step === 1 && form1 && form2) ||
      (step === 2 && form3) ||
      (step === 3 && (paymentMethod === "UNIXCARD" || paymentOption === "installment")) 
  
    setFormState("showError", !nextStep)
    if (nextStep) updatePlanState({ step: step + 1 })
  }

  useEffect(() => {
    if (cartInfo) {
      const resolucao = resolveCartPlan({ plans, idPlano: cartInfo.id_plano, loadingPlan, chipOnly })
      if (resolucao.acao === 'aguardar') return

      // Plano do carrinho ocultado do link de vendas: a compra recomeça na
      // escolha de plano. Sair do checkout com a compra ativa faz o Plans
      // descartar hash, carrinho e cupom.
      if (resolucao.acao === 'reiniciar') {
        limparCheckout()
        updatePlanState({ currentPlan: plans[0] ?? defaultPlan, planIndex: 0, chosenPlan: 'monthly' })
        notify(t.errors.planUnavailable, "error")
        navigate('/')
        return
      }

      updatePlanState({
        virtualChip: cartInfo.esim === '1' ? true : false,
        currentPlan: resolucao.plano,
        portability: cartInfo.deseja_portabilidade === '1' ? true : false,
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[cartInfo, plans])
  
  // F5 depois do OTP: volta direto à tela de cartão da compra guardada.
  useEffect(() => {
    if (retomadaTentada) return
    retomadaTentada = true

    const salvo = lerCheckout()
    if (!salvo) return

    updateFormState({ hash: salvo.hash, cartId: salvo.cartId, paymentMethod: 'CARTAO', paymentOption: 'oneTime' })
    const { planIndex, chosenPlan, pickUpChip, smartControl, portability, chipOnly, chipQuantity } = salvo.plano
    updatePlanState({ step: 4, planIndex, chosenPlan, pickUpChip, chipOnly, chipQuantity })
    // Pelos setters, que também recalculam planBonus e planConfigs.
    setvirtualChip(salvo.virtualChip)
    setPortability(portability)
    setSmartControl(smartControl)
    updateInitState({
      ...(salvo.cupom && {
        couponText: salvo.cupom.texto,
        couponValid: true,
        couponValue: salvo.cupom.valor,
        couponPercent: salvo.cupom.percentual,
      }),
      // O carrinho devolve plano, eSIM, portabilidade e os dados pessoais (efeito
      // `cartInfo`). Na compra de chip avulso não há plano para o `cartInfo`
      // aplicar, e o resumo usa só os campos guardados.
      cartSearchLiberated: !salvo.plano.chipOnly,
    })
    setRetomado(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Prazo da compra acabou: um F5 não deve voltar a ela.
  useEffect(() => {
    const expiraEm = purchaseSummaryWithOtp?.expira_em
    if (expirado && expiraEm && new Date(expiraEm).getTime() <= Date.now()) limparCheckout()
  }, [expirado, purchaseSummaryWithOtp])

  useEffect(() => {
    let ativo = true
    updateFormState({ errorSummary: null, loadingSummary: true })
    async function carregar() {
      if (!hash) {
        updateFormState({
          errorSummary: t.errors.purchaseExpired,
          loadingSummary: false
        })
        return
      }
      updateFormState({ loadingSummary: true })
      try {
        const { status, body, error } = await checkoutResumo(hash)
        if (!ativo) return

        if (status >= 200 && status < 300 && body?.retorno) {
          updateFormState({ purchaseSummaryWithOtp: body })
        } else if (error) {
          // 404/409 = a compra é inválida (mensagem real da API);
          // demais status = falha técnica, que não invalida a compra.
          const compraInvalida = error.status === 404 || error.status === 409
          if (compraInvalida) limparCheckout()

          updateFormState({
            errorSummary: mensagemDeErro(
              error,
              compraInvalida ? t.errors.purchaseExpired : t.errors.summaryUnavailable
            )
          })
        } else {
          limparCheckout()
          updateFormState({ errorSummary: t.errors.purchaseExpired })
        }
      } catch (error) {
        console.error(error)

        if (ativo) {
          updateFormState({
            errorSummary: mensagemDeErro(normalizeApiError(error), t.errors.summaryUnavailable)
          })
        }
      } finally {
        updateFormState({ loadingSummary: false })
      }
    }
    carregar()
    return () => {
      ativo = false
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash])
  
  useEffect(() => {
    const disabledPaymentCases =
      step === 4 &&
      paymentOption !== "installment" &&
      paymentMethod !== "UNIXCARD" &&
      (!formValido || expirado || pagando || tentativas <= 0 || (paymentMethod === "CARTAO" && valorCartao > 0 && !tresDSPronto))
    
    setFormState('disabledBtn', disabledPaymentCases)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, paymentOption, formValido, expirado, pagando, tentativas, tresDSPronto, valorCartao])

  const tresDSInicializadoRef = useRef(false)
  useEffect(() => {
    if (
      step === 4 &&
      paymentMethod === "CARTAO" &&
      paymentOption !== "installment" &&
      valorCartao > 0 &&
      !tresDSInicializadoRef.current
    ) {
      // O SDK pode só estar demorando: tenta de novo por alguns segundos antes
      // de tratá-lo como ausente, e avisa antes de qualquer interação.
      return esperarSdk3DS(
        () => {
          if (!inicializar(valorCartao)) return false
          tresDSInicializadoRef.current = true
          setTresDSPronto(true)
          return true
        },
        () => {
          console.error("SDK 3DS (window.Safe2Pay.Mpi) ausente: pagamento com cartão indisponível.")
          updateFormState({ sdk3DSIndisponivel: true })
        }
      )
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, paymentMethod, paymentOption, valorCartao])

  const { inicializar, iniciarDesafio } = use3DS({
    onSuccess: (threeDS: ThreeDS) => {
      if (!pagamentoAtivoRef.current) return
      finalizarPagamento(threeDS)
    },
    onFailure: () => {
      if (!pagamentoAtivoRef.current) return
      notify(t.errors.threeDSDeclined, "error")
      encerrarPagamento()
    },
    // Os dois encerram a tentativa sem consumir uma das tentativas: nada foi
    // cobrado e a compra segue válida para outro cartão.
    onUnenrolled: () => {
      if (!pagamentoAtivoRef.current) return
      notify(t.errors.threeDSUnenrolled, "error")
      encerrarPagamento()
    },
    onUnsupportedBrand: () => {
      if (!pagamentoAtivoRef.current) return
      notify(t.errors.threeDSUnsupportedBrand, "error")
      encerrarPagamento()
    },
    onError: () => {
      if (!pagamentoAtivoRef.current) {
        console.warn("3DS onError fora de pagamento ativo (handshake do Init) — ignorado.")
        return
      }
      notify(t.errors.threeDSTechnicalError, "error")
      encerrarPagamento()
    },
    onTimeout: () => {
      // Nada foi cobrado: /checkout/cartao/pagar não chegou a ser chamado, então
      // a tentativa não é consumida e o hash_unico segue válido.
      if (!pagamentoAtivoRef.current) return
      notify(t.errors.threeDSTimeout, "error")
      encerrarPagamento()
    },
  })

  function encerrarPagamento() {
    setPagando(false)
    pagamentoAtivoRef.current = false
    if (resolvePagamentoRef.current) {
      resolvePagamentoRef.current()
      resolvePagamentoRef.current = null
    }
  }

  async function handlePagar(): Promise<void> {
    if (pagando || !formValido || expirado || tentativas <= 0) return
    if (!cardForm) {
      setFormState("showError", true)
      return
    }

    // Sem 3DS o pagamento não conclui: sai antes de tokenizar o cartão.
    if (valorCartao > 0 && !tresDSPronto) {
      notify(t.errors.threeDSUnavailable, "error")
      return
    }

    setPagando(true)
    pagamentoAtivoRef.current = true

    // Retentativa com o mesmo cartão nesta compra: reaproveita o token em vez de
    // tokenizar de novo.
    let token = cacheToken.buscar(hash, cardRegisterJson)
    if (!token) {
      try {
        const registro = await fetchCardRegister(cardRegisterJson)
        if (!registro?.dados?.token) {
          notify(registro?.mensagem || t.errors.cardRegisterUnknown, "error")
          encerrarPagamento()
          return
        }
        token = registro.dados.token
        cacheToken.guardar(hash, cardRegisterJson, token)
      } catch (error) {
        const apiError = normalizeApiError(error)
        console.error(apiError)
        notify(mensagemDeErro(apiError, t.errors.cardRegisterUnknown), "error")
        encerrarPagamento()
        return
      }
    }

    tokenCartaoRef.current = token

    const mesAno = dayjs(cardIssueDate).format("MM/YYYY")
    const [mes, ano] = mesAno.split("/")

    const iniciou = iniciarDesafio({
      cartao: {
        numero: cardNumber.replace(/ /g, ""),
        mes,
        ano,
        titular: cardName,
      },
    })

    if (!iniciou) {
      notify(t.errors.threeDSStartFailed, "error")
      encerrarPagamento()
      return
    }

    return new Promise<void>((resolve) => {
      resolvePagamentoRef.current = resolve
    })
  }

  async function finalizarPagamento(threeDS: ThreeDS): Promise<void> {
    try {
      const { body, status, error } = await cartaoPagar({
        hash_unico: hash,
        cartao: {
          titular: cardName,
          numero: cardNumber.replace(/ /g, ""),
          data_expiracao: dayjs(cardIssueDate).format("MM/YYYY"),
          codigo_verificacao: cardSecurityCode,
        },
        "3DS": threeDS,
        creditCardToken: tokenCartaoRef.current ?? undefined,
      })

      if (isPaymentApproved(status, body)) {
        limparCheckout()
        const clientId = publicSaleId(body, virtualChip)
        try {
          await SuccessfullyPaid({ cartId, clientId, virtualChip, navigate })
        } catch (postError) {
          console.error("Erro pós-pagamento:", postError)
          notify(t.page2.payment.paymentApproved, "success")
          navigate(`/configure-sua-conta?id=${clientId}&tipo=${virtualChip ? 'A' : 'L'}`)
        }
        return
      }

      const apiError = error ?? normalizeApiError({ response: { status } })
      const { encerra, consomeTentativa } = classifyPaymentFailure(apiError)

      const fallback = apiError.status === 503
        ? t.errors.paymentUnavailable
        : t.errors.paymentDeclined
      const motivo = [
        mensagemDeErro(apiError, fallback),
        mensagemDeEspera(apiError)
      ].filter(Boolean).join(' ')

      if (encerra) {
        limparCheckout()
        notify(motivo, "error")
        navigate('/')
        return
      }

      if (!consomeTentativa) {
        notify(motivo, "error")
        return
      }

      const restantes = tentativas - 1
      setTentativas(restantes)

      if (restantes <= 0) {
        limparCheckout()
        notify(t.errors.paymentAttemptsOver, "error")
        navigate('/')
      } else {
        notify(motivo, "error")
      }
    } catch (error) {
      console.error(error)
      notify(mensagemDeErro(normalizeApiError(error), t.errors.paymentDeclined), "error")
    } finally {
      tokenCartaoRef.current = null
      encerrarPagamento()
    }
  }

  return (
    <Grid container item className={name ? `${name} purchase-summary` : "purchase-summary"} xs={12}>
      <Grid item className="priceArea">
        {loadingPlan ? (
          <Skeleton className="skltn-price" sx={{ borderRadius: '4px' }} variant="rounded" width="70px" height="26px" />
        ) : (
          <h3>R$ {valorCartao.toFixed(2).replace('.', ',')}</h3>
        )}
        <p>{text[0]}</p>
        {unixcardParcelado && !loadingPlan && (
          <p className="installmentsLine">
            {interpolate(t.page2.payment.installmentOption, {
              n: numeroParcelas,
              valor: formatarReais(valorParcela(valorCartao, numeroParcelas))
            })}
          </p>
        )}
      </Grid>
      <Grid item className="buttonArea">
        {reqLoading || pagando ? (
          <CircularProgress />
        ) : (
          <Button
            className={disabledBtn ? 'disabled' : ''}
            onClick={handleNext}
            disabled={disabledBtn}
            style={{ textTransform: 'none', opacity: disabledBtn ? '0.8' : '1' }}
          >
            {step === 4 ? (paymentMethod === "BOLETO" ? t.common.finish : text[2]) : text[1]}
            <RightArrow />
          </Button>
        )}
      </Grid>
      {otpData && (
        <OtpModal
          key={otpData.otp_session_token}
          open={otpOpen}
          initData={otpData}
          onSuccess={handleOtpSuccess}
          onResend={handleOtpResend}
          onCancel={handleOtpCancel}
          onError={handleOtpError}
        />
      )}
    </Grid>
  )
}